import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { grid } from '@shared/styles';
import { useIsDesktop, usePersistentRef } from '@shared/util/hooks';

export interface Position {
  x: number;
  y: number;
}

interface BottomSheetAPI {
  reset(): void;
  setContentAt(node: ReactNode, position: Position, onClose?: () => void): void;
  updatePosition(position: Position): void;
  refreshHeight(): void;
}

const defaultBottomSheetAPI: BottomSheetAPI = {
  reset: () => {},
  setContentAt: () => {},
  updatePosition: () => {},
  refreshHeight: () => {},
};

export const BottomSheetContext = createContext<BottomSheetAPI>(defaultBottomSheetAPI);

interface Props {
  children: ReactNode;
}

export function MBBottomSheetProvider({ children }: Props) {
  const [content, setContent] = useState<ReactNode>();
  const [height, setHeight] = useState<number>();
  const [position, setPosition] = useState<Position>({ x: -9999, y: -9999 });
  const positionRef = usePersistentRef(position);
  const previousPositionRef = useRef<Position>(position);
  const handleClose = useRef<VoidFunction>();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    previousPositionRef.current = position;
  }, [position]);

  const handleClick = useCallback(e => {
    e.stopPropagation();
    handleClose.current?.();
    setContent(null);
  }, []);

  const eatClick = useCallback(event => event.stopPropagation(), []);

  const innerRef = useRef<HTMLDivElement | null>(null);
  const refreshHeight = useCallback(() => {
    if (innerRef.current) {
      try {
        const rect = innerRef.current?.getBoundingClientRect();
        const bodyRect = document.body.getBoundingClientRect();
        if (rect.height + positionRef.current.y > bodyRect.height) {
          // content overflows so set a height for the container to be 8px from bottom of page
          setHeight(bodyRect.height - positionRef.current.y - grid(4));
        }
      } catch (e) {
        console.log('could not position bottom sheet', e);
      }
    }
  }, [positionRef]);

  useEffect(() => {
    refreshHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  const value = useMemo<BottomSheetAPI>(
    () => ({
      reset: () => {
        setContent(null);
        setPosition({ x: 0, y: 0 });
        setHeight(undefined);
        handleClose.current = undefined;
      },
      setContentAt: (node: ReactNode, pos: Position, onClose: () => void) => {
        setContent(node);
        if (pos) {
          setPosition(pos);
        }
        handleClose.current = onClose;
      },
      updatePosition: setPosition,
      refreshHeight,
    }),
    [setContent, refreshHeight],
  );

  const renderContent = (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0)',
        zIndex: 1000,
      }}
      onClick={handleClick}
      data-testid="bottom-sheet-overlay"
    >
      <div
        ref={innerRef}
        onClick={eatClick}
        style={{
          position: 'absolute',
          top: position.y,
          left: position.x,
          height: height === null ? 'auto' : height,
          transition: previousPositionRef.current.y !== -9999 ? 'top 150ms ease-out' : undefined,
        }}
      >
        {content}
      </div>
    </div>
  );

  return (
    <BottomSheetContext.Provider value={value}>
      {children}
      {content && isDesktop && ReactDOM.createPortal(renderContent, document.body)}
      {content && !isDesktop && renderContent}
    </BottomSheetContext.Provider>
  );
}
